import { useState, useEffect } from "react";
import { Plus, Search, Edit2, Trash2, X, Image, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", description: "", category: "", subcategory: "", brand: "", manufacturer: "", composition: "", form: "Tablet", pack_sizes: [], images: [], requires_prescription: false, stock_qty: 100, tags: [], mrp: 0, sale_price: 0, discount_percent: 0, key_uses: "", how_to_use: "", side_effects: "", safety_info: "", hsn_code: "", gst_percent: 5 };
const CATEGORIES = ["Medicines", "Personal Care", "Health Conditions", "Devices"];
const FORMS = ["Tablet", "Capsule", "Syrup", "Liquid", "Paste", "Powder", "Inhaler", "Spray", "Device", "Oil", "Chewable Tablet", "Cream", "Gel", "Drops", "Injection"];

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [formTab, setFormTab] = useState("basic");
  const [newImageUrl, setNewImageUrl] = useState("");
  const [newPackSize, setNewPackSize] = useState({ size: "", price: 0, mrp: 0 });
  const [newTag, setNewTag] = useState("");

  const fetchProducts = () => {
    setLoading(true);
    axios.get(`${API}/admin/products?page=${page}&search=${search}`, { withCredentials: true })
      .then(r => { setProducts(r.data.products || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchProducts(); }, [page, search]);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setFormTab("basic"); setShowDialog(true); };
  const openEdit = (p) => {
    setEditing(p.product_id);
    setForm({ ...EMPTY, ...p, tags: p.tags || [], images: p.images || [], pack_sizes: p.pack_sizes || [] });
    setFormTab("basic");
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name) { toast.error("Product name is required"); return; }
    try {
      const payload = { ...form };
      if (payload.mrp > 0 && payload.sale_price > 0) {
        payload.discount_percent = Math.round((payload.mrp - payload.sale_price) / payload.mrp * 100);
      }
      if (editing) {
        await axios.put(`${API}/admin/products/${editing}`, payload, { withCredentials: true });
        toast.success("Product updated!");
      } else {
        await axios.post(`${API}/admin/products`, payload, { withCredentials: true });
        toast.success("Product created!");
      }
      setShowDialog(false);
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await axios.delete(`${API}/admin/products/${id}`, { withCredentials: true }); toast.success("Deleted"); fetchProducts(); } catch { toast.error("Failed"); }
  };

  const u = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const addImage = () => { if (newImageUrl.trim()) { u("images", [...(form.images || []), newImageUrl.trim()]); setNewImageUrl(""); } };
  const removeImage = (i) => { u("images", form.images.filter((_, idx) => idx !== i)); };
  const addPackSize = () => { if (newPackSize.size) { u("pack_sizes", [...(form.pack_sizes || []), { ...newPackSize }]); setNewPackSize({ size: "", price: 0, mrp: 0 }); } };
  const removePackSize = (i) => { u("pack_sizes", form.pack_sizes.filter((_, idx) => idx !== i)); };
  const addTag = () => { if (newTag.trim() && !form.tags.includes(newTag.trim())) { u("tags", [...(form.tags || []), newTag.trim()]); setNewTag(""); } };
  const removeTag = (t) => { u("tags", form.tags.filter(tag => tag !== t)); };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Products ({total})</h1>
        <Button onClick={openAdd} className="rounded-full bg-cyan-600 hover:bg-cyan-700" data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1" /> Add Product</Button>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search products..." className="pl-10 rounded-xl" data-testid="admin-search-products" />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <table className="w-full text-sm" data-testid="products-table">
          <thead><tr className="text-left text-xs text-slate-500 border-b bg-slate-50"><th className="p-4">Product</th><th className="p-4">Category</th><th className="p-4">Price</th><th className="p-4">Stock</th><th className="p-4">Status</th><th className="p-4">Actions</th></tr></thead>
          <tbody>
            {loading ? [...Array(5)].map((_, i) => <tr key={i}><td colSpan={6} className="p-4"><div className="h-10 skeleton-loading rounded" /></td></tr>) :
            products.map(p => (
              <tr key={p.product_id} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="p-4"><div className="flex items-center gap-3"><img src={p.images?.[0] || ""} alt="" className="w-10 h-10 rounded-lg object-cover bg-slate-100" /><div><p className="font-medium text-slate-800 text-sm">{p.name}</p><p className="text-xs text-slate-500">{p.brand}</p></div></div></td>
                <td className="p-4 text-slate-600 text-xs">{p.category}<br/><span className="text-slate-400">{p.subcategory}</span></td>
                <td className="p-4"><span className="font-semibold text-xs">Rs.{p.sale_price}</span><br/><span className="text-[10px] text-slate-400 line-through">Rs.{p.mrp}</span></td>
                <td className="p-4"><span className={`font-medium text-xs ${p.stock_qty < 20 ? 'text-red-500' : 'text-emerald-600'}`}>{p.stock_qty}</span></td>
                <td className="p-4"><Badge className={`text-[10px] ${p.in_stock ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{p.in_stock ? 'Active' : 'Out'}</Badge></td>
                <td className="p-4"><div className="flex gap-1"><button onClick={() => openEdit(p)} className="p-1.5 hover:bg-cyan-50 rounded-lg text-slate-500 hover:text-cyan-600" data-testid={`edit-${p.product_id}`}><Edit2 className="w-4 h-4" /></button><button onClick={() => deleteProduct(p.product_id)} className="p-1.5 hover:bg-red-50 rounded-lg text-slate-500 hover:text-red-500" data-testid={`delete-${p.product_id}`}><Trash2 className="w-4 h-4" /></button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Product Form Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto rounded-2xl">
          <DialogHeader><DialogTitle className="text-lg">{editing ? "Edit Product" : "Add New Product"}</DialogTitle></DialogHeader>

          <Tabs value={formTab} onValueChange={setFormTab}>
            <TabsList className="bg-slate-50 rounded-xl w-full flex-wrap h-auto gap-1 p-1">
              <TabsTrigger value="basic" className="rounded-lg text-xs flex-1">Basic Info</TabsTrigger>
              <TabsTrigger value="pricing" className="rounded-lg text-xs flex-1">Pricing & Stock</TabsTrigger>
              <TabsTrigger value="images" className="rounded-lg text-xs flex-1">Images</TabsTrigger>
              <TabsTrigger value="details" className="rounded-lg text-xs flex-1">Details</TabsTrigger>
              <TabsTrigger value="medical" className="rounded-lg text-xs flex-1">Medical Info</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs font-semibold">Product Name *</Label><Input value={form.name} onChange={e => u("name", e.target.value)} className="rounded-xl mt-1" placeholder="e.g. Dolo 650mg" data-testid="product-name-input" /></div>
                <div><Label className="text-xs font-semibold">Brand</Label><Input value={form.brand} onChange={e => u("brand", e.target.value)} className="rounded-xl mt-1" placeholder="e.g. Micro Labs" data-testid="product-brand-input" /></div>
              </div>
              <div><Label className="text-xs font-semibold">Description</Label><Textarea value={form.description} onChange={e => u("description", e.target.value)} className="rounded-xl mt-1" rows={3} placeholder="Detailed product description..." /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label className="text-xs font-semibold">Category</Label>
                  <Select value={form.category} onValueChange={v => u("category", v)}><SelectTrigger className="rounded-xl mt-1"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label className="text-xs font-semibold">Subcategory</Label><Input value={form.subcategory} onChange={e => u("subcategory", e.target.value)} className="rounded-xl mt-1" placeholder="e.g. OTC" /></div>
                <div><Label className="text-xs font-semibold">Form</Label>
                  <Select value={form.form} onValueChange={v => u("form", v)}><SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger><SelectContent>{FORMS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs font-semibold">Manufacturer</Label><Input value={form.manufacturer} onChange={e => u("manufacturer", e.target.value)} className="rounded-xl mt-1" /></div>
                <div><Label className="text-xs font-semibold">Composition / Salt</Label><Input value={form.composition} onChange={e => u("composition", e.target.value)} className="rounded-xl mt-1" placeholder="e.g. Paracetamol 650mg" /></div>
              </div>
              {/* Tags */}
              <div>
                <Label className="text-xs font-semibold">Tags</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={newTag} onChange={e => setNewTag(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} className="rounded-xl flex-1" placeholder="Add tag and press Enter" />
                  <Button type="button" onClick={addTag} variant="outline" size="sm" className="rounded-xl"><Plus className="w-3 h-3" /></Button>
                </div>
                {form.tags?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{form.tags.map(t => <Badge key={t} className="bg-cyan-50 text-cyan-700 text-xs cursor-pointer hover:bg-red-50 hover:text-red-600" onClick={() => removeTag(t)}>{t} <X className="w-3 h-3 ml-1" /></Badge>)}</div>}
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl">
                <Switch checked={form.requires_prescription} onCheckedChange={v => u("requires_prescription", v)} data-testid="product-rx-toggle" />
                <div><Label className="text-xs font-semibold">Requires Prescription</Label><p className="text-[10px] text-slate-500">Mark if this medicine needs a valid Rx</p></div>
              </div>
            </TabsContent>

            <TabsContent value="pricing" className="space-y-4 mt-4">
              <div className="grid grid-cols-3 gap-4">
                <div><Label className="text-xs font-semibold">MRP (Rs.) *</Label><Input type="number" value={form.mrp} onChange={e => u("mrp", parseFloat(e.target.value) || 0)} className="rounded-xl mt-1" data-testid="product-mrp-input" /></div>
                <div><Label className="text-xs font-semibold">Sale Price (Rs.) *</Label><Input type="number" value={form.sale_price} onChange={e => u("sale_price", parseFloat(e.target.value) || 0)} className="rounded-xl mt-1" data-testid="product-price-input" /></div>
                <div><Label className="text-xs font-semibold">Stock Qty</Label><Input type="number" value={form.stock_qty} onChange={e => u("stock_qty", parseInt(e.target.value) || 0)} className="rounded-xl mt-1" data-testid="product-stock-input" /></div>
              </div>
              {form.mrp > 0 && form.sale_price > 0 && (
                <div className="p-3 bg-emerald-50 rounded-xl text-sm text-emerald-700">Discount: <strong>{Math.round((form.mrp - form.sale_price) / form.mrp * 100)}% OFF</strong> (Save Rs.{(form.mrp - form.sale_price).toFixed(2)})</div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs font-semibold">HSN Code</Label><Input value={form.hsn_code} onChange={e => u("hsn_code", e.target.value)} className="rounded-xl mt-1" /></div>
                <div><Label className="text-xs font-semibold">GST %</Label><Input type="number" value={form.gst_percent} onChange={e => u("gst_percent", parseFloat(e.target.value) || 0)} className="rounded-xl mt-1" /></div>
              </div>
              {/* Pack Sizes */}
              <div>
                <Label className="text-xs font-semibold">Pack Sizes</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={newPackSize.size} onChange={e => setNewPackSize({...newPackSize, size: e.target.value})} className="rounded-xl flex-1" placeholder="e.g. Strip of 15" />
                  <Input type="number" value={newPackSize.price || ""} onChange={e => setNewPackSize({...newPackSize, price: parseFloat(e.target.value) || 0})} className="rounded-xl w-24" placeholder="Price" />
                  <Input type="number" value={newPackSize.mrp || ""} onChange={e => setNewPackSize({...newPackSize, mrp: parseFloat(e.target.value) || 0})} className="rounded-xl w-24" placeholder="MRP" />
                  <Button type="button" onClick={addPackSize} variant="outline" size="sm" className="rounded-xl"><Plus className="w-3 h-3" /></Button>
                </div>
                {form.pack_sizes?.length > 0 && <div className="space-y-1 mt-2">{form.pack_sizes.map((ps, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-xs">
                    <span>{ps.size} - Rs.{ps.price} (MRP: Rs.{ps.mrp})</span>
                    <button onClick={() => removePackSize(i)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                  </div>
                ))}</div>}
              </div>
            </TabsContent>

            <TabsContent value="images" className="space-y-4 mt-4">
              <div>
                <Label className="text-xs font-semibold">Product Images (URLs)</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={newImageUrl} onChange={e => setNewImageUrl(e.target.value)} className="rounded-xl flex-1" placeholder="https://example.com/image.jpg" />
                  <Button type="button" onClick={addImage} variant="outline" className="rounded-xl"><Image className="w-4 h-4 mr-1" /> Add</Button>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Add image URLs. Leave empty for auto-generated placeholder.</p>
              </div>
              {form.images?.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {form.images.map((img, i) => (
                    <div key={i} className="relative group rounded-xl overflow-hidden border">
                      <img src={img} alt={`Product ${i+1}`} className="w-full aspect-square object-cover" />
                      <button onClick={() => removeImage(i)} className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                      {i === 0 && <Badge className="absolute bottom-2 left-2 bg-cyan-600 text-[10px]">Primary</Badge>}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="details" className="space-y-4 mt-4">
              <div><Label className="text-xs font-semibold">Key Uses</Label><Textarea value={form.key_uses} onChange={e => u("key_uses", e.target.value)} className="rounded-xl mt-1" rows={4} placeholder="Enter each use on a new line:&#10;Relieves fever and headache&#10;Reduces body pain&#10;Effective for toothache" /></div>
              <div><Label className="text-xs font-semibold">How to Use / Directions</Label><Textarea value={form.how_to_use} onChange={e => u("how_to_use", e.target.value)} className="rounded-xl mt-1" rows={4} placeholder="Enter each step on a new line:&#10;Take 1 tablet with water&#10;Do not exceed 4 tablets in 24 hours&#10;Can be taken with or without food" /></div>
            </TabsContent>

            <TabsContent value="medical" className="space-y-4 mt-4">
              <div><Label className="text-xs font-semibold">Side Effects</Label><Textarea value={form.side_effects} onChange={e => u("side_effects", e.target.value)} className="rounded-xl mt-1" rows={4} placeholder="Enter each side effect on a new line:&#10;Nausea&#10;Stomach upset&#10;Allergic reactions (rare)" /></div>
              <div><Label className="text-xs font-semibold">Safety Information</Label><Textarea value={form.safety_info} onChange={e => u("safety_info", e.target.value)} className="rounded-xl mt-1" rows={4} placeholder="Storage instructions, warnings, contraindications..." /></div>
            </TabsContent>
          </Tabs>

          <div className="flex gap-2 justify-between mt-4 pt-4 border-t">
            <div className="flex gap-1">
              {["basic", "pricing", "images", "details", "medical"].map((t, i) => (
                <div key={t} className={`w-2 h-2 rounded-full ${formTab === t ? 'bg-cyan-600' : 'bg-slate-200'}`} />
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-full">Cancel</Button>
              <Button onClick={save} className="rounded-full bg-cyan-600 hover:bg-cyan-700" data-testid="save-product-btn">{editing ? "Update" : "Create"} Product</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
